import React from "react";
import { useTable, useFilters, useSortBy, usePagination } from "react-table";
import classnames from "classnames";
import { matchSorter } from "match-sorter";
import { makeStyles } from "@material-ui/core/styles";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import CustomInput from "components/Forms/CustomInput/CustomInput.js";
import GridContainer from "components/UI/Grid/GridContainer.js";
import GridItem from "components/UI/Grid/GridItem.js";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { getBadgeFromStatus } from "utils/functions";

import styles from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";

const newStyles = {
  ...styles,
  formControlMargins: {
    margin: "3px 0 !important",
  },
  gridContainer: {
    justifyContent: "center",
  },
};

const useStyles = makeStyles(newStyles);

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
}

fuzzyTextFilterFn.autoRemove = (val) => !val;

function Table(props, ref = undefined) {
  const {
    columns,
    data,
    initialPageSize = 20,
    exportToCSVFunction,
    filters,
    setFilters,
    cacheFilters = false,
    sortingInitial,
    setSortingInitial,
    id,
    validationStatuses,
    columnsNotTrimFilters,
  } = props;

  const [numberOfRows, setNumberOfRows] = React.useState(initialPageSize);
  const [pageSelect, handlePageSelect] = React.useState(0);
  const classes = useStyles();
  const { t } = useTranslation("common");

  const filterTypes = React.useMemo(
    () => ({
      fuzzyText: fuzzyTextFilterFn,
    }),
    [],
  );

  const defaultColumn = React.useMemo(
    () => ({
      Filter: (props) => <DefaultColumnFilter {...props} statusFilterOptions={props.statusFilterOptions} />,
    }),
    [props.statusFilterOptions]
  );
  
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    pageOptions,
    previousPage,
    canPreviousPage,
    canNextPage,
    setPageSize,
    gotoPage,
    rows,
    state,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      manualFilters: false,
      validationStatuses,
      initialState: {
        pageSize: initialPageSize,
        pageIndex: 0,
        filters: cacheFilters ? filters : [],
        sortBy: sortingInitial ?? [],
      },
    },
    useFilters,
    useSortBy,
    usePagination,
  );

  React.useEffect(() => {
    cacheFilters && setFilters(state.filters);
  }, [state, setFilters]);

  React.useEffect(() => {
    sortingInitial != state.sortBy &&
      setSortingInitial &&
      setSortingInitial(state.sortBy);
  }, [state, sortingInitial, setSortingInitial]);

  function DefaultColumnFilter({
    column: { filterValue, preFilteredRows, setFilter, id, statusFilterOptions },
  }) {
    const count = preFilteredRows?.length;
    const { t } = useTranslation("common");

    const fgStatus = [
      "WAITING_FOR_UPDATE",
      "REJECTED",
      "COMPLETED",
      "PENDING",
      "CLOSED",
      "ACTIVE",
    ];

    const uvStatus = [
      "APPROVED",
      "REJECTED",
      "MANUAL_VALIDATION",
      "VALIDATION_PENDING",
      "WAITING_FOR_DOWNLOAD",
      "WAITING_FOR_APPROVAL",
      "SUPPLIER_DOWNLOADED",
      "SUPPLIER_DOWNLOAD",
      "WAITING_DOCUMENTS",
      "UPLOADING_DOCUMENTS",
      "UPLOADING_DOCUMENTS_BUYER",
      "UPLOADING_DOCUMENTS_SUPPLIER",
      "READ_ONLY",
    ];

    const ActivityItemStatusEnum = {
      APPROVED: "Approved",
      REJECTED: "Rejected",
      MANUAL_VALIDATION: "MANUAL_VALIDATION",
      VALIDATION_PENDING: "VALIDATION PENDING",
      WAITING_FOR_DOWNLOAD: "WAITING_FOR_DOWNLOAD",
      WAITING_DOCUMENTS: "WAITING_DOCUMENTS",
      WAITING_FOR_APPROVAL: "WAITING_FOR_APPROVAL",
      SUPPLIER_DOWNLOADED: "SUPPLIER_DOWNLOADED",
      SUPPLIER_DOWNLOAD: "SUPPLIER_DOWNLOAD",
      UPLOADING_DOCUMENTS: "Uploading Documents",
      UPLOADING_DOCUMENTS_BUYER: "UPLOADING_DOCUMENTS_BUYER",
      UPLOADING_DOCUMENTS_SUPPLIER: "UPLOADING_DOCUMENTS_SUPPLIER",
      READ_ONLY: "READ_ONLY",
    };

    const ActivityItemValidationStatusEnum = {
      COMPLETED: "COMPLETED",
      REJECTED: "REJECTED",
      WAITING_FOR_UPDATE: "WAITING_FOR_UPDATE",
      CLOSED: "CLOSED",
      PENDING: "PENDING",
      ACTIVE: "ACTIVE",
    };

     if (id === "status" && statusFilterOptions) {
      return (
      <FormControl fullWidth>
          <Select
            style={{
              padding: "3% 0 0 7%",
              display: "flex",
              flexDirection: "column",
              opacity: filterValue ? 1 : 0.5,
            }}
            variant="standard"
            value={filterValue ?? ""}
            onChange={(e) => setFilter(e.target.value || undefined)}
          >
            {statusFilterOptions?.status?.map((status) => (
              <div
                style={{ padding: "5px 10px", cursor: "pointer" }}
                key={status}
                value={t(statusFilterOptions?.enum[status], { ns: "status" })}
              >
                {getBadgeFromStatus(status, (text) =>
                  t(statusFilterOptions?.enum[text], { ns: "status" }),
                )}
              </div>
            ))}
            <div style={{ padding: "5px 10px", cursor: "pointer" }} value="">
              {t("clear", { ns: "status" })}
            </div>
          </Select>
        </FormControl>
      );
    }

    if (id === "result") {
      return (
        <FormControl fullWidth>
          <Select
            style={{
              padding: "3% 0 0 7%",
              display: "flex",
              flexDirection: "column",
              opacity: filterValue ? 1 : 0.5,
            }}
            variant="standard"
            value={filterValue ?? ""}
            onChange={(e) => setFilter(e.target.value || undefined)}
          >
            {uvStatus?.map((status) => (
              <div
                style={{ padding: "5px 10px", cursor: "pointer" }}
                key={status}
                value={t(ActivityItemStatusEnum[status], { ns: "status" })}
              >
                {getBadgeFromStatus(status, (text) =>
                  t(ActivityItemStatusEnum[text], { ns: "status" }),
                )}
              </div>
            ))}
            <div style={{ padding: "5px 10px", cursor: "pointer" }} value="">
              {t("clear", { ns: "status" })}
            </div>
          </Select>
        </FormControl>
      );
    }

    if (id === "status") {
      return (
        <FormControl fullWidth>
          <Select
            style={{
              padding: "3% 0 0 7%",
              display: "flex",
              flexDirection: "column",
              opacity: filterValue ? 1 : 0.5,
            }}
            variant="standard"
            value={filterValue ?? ""}
            onChange={(e) => setFilter(e.target.value || undefined)}
          >
            {fgStatus?.map((status) => (
              <div
                style={{ padding: "5px 10px", cursor: "pointer" }}
                key={status}
                value={t(ActivityItemValidationStatusEnum[status], {
                  ns: "status",
                })}
              >
                {getBadgeFromStatus(status, (text) =>
                  t(ActivityItemValidationStatusEnum[text], { ns: "status" }),
                )}
              </div>
            ))}
            <div style={{ padding: "5px 10px", cursor: "pointer" }} value="">
              {t("clear", { ns: "status" })}
            </div>
          </Select>
        </FormControl>
      );
    }

    return (
      <CustomInput
        formControlProps={{
          fullWidth: true,
        }}
        inputProps={{
          value: filterValue || "",
          onChange: (e) => {
            const trimmedColumns = columnsNotTrimFilters?.includes(id) ?? false;

            const trimmedValue =
              !trimmedColumns && typeof e.target.value === "string"
                ? e.target.value.trim()
                : e.target.value;

            setFilter(trimmedValue || undefined);
          },
          placeholder: `${t("react-search")} ${count} ${t("react-records")}...`,
          "data-cy": "table-filter", // Cypress data attribute
        }}
      />
    );
  }

  let pageSelectData = Array.apply(
    null,
    Array(pageOptions.length),
  ).map(function () {});
  let numberOfRowsData = [5, 10, 20, 25, 50, 100];
  return (
    <div className="ReactTable -striped -highlight">
      <table
        {...getTableProps()}
        ref={ref}
        className="rt-table"
        id={id}
        data-cy={props["data-cy"] ?? "data-table"}
      >
        {" "}
        {/* Add data-cy */}
        <thead className="rt-thead -header">
          {headerGroups.map((headerGroup, index) => (
            <tr
              key={index}
              {...headerGroup.getHeaderGroupProps()}
              className="rt-tr"
            >
              {headerGroup.headers.map((column, key) => {
                return (
                  <th
                    key={key}
                    className={classnames("rt-th rt-resizable-header", {
                      "-cursor-pointer": headerGroup.headers.length - 1 !== key,
                      "-sort-asc": column.isSorted && !column.isSortedDesc,
                      "-sort-desc": column.isSorted && column.isSortedDesc,
                    })}
                  >
                    <div
                      className="rt-resizable-header-content"
                      {...column.getSortByToggleProps()}
                      data-cy={`header-${column.id}`} // Add data-cy
                    >
                      {column.render("Header")}
                    </div>
                    <div>
                      {headerGroup.headers.length - 1 === key
                        ? null
                        : column.canFilter
                        ? column.render("Filter")
                        : null}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="rt-tbody">
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                className={classnames(
                  "rt-tr",
                  { " -odd": i % 2 === 0 },
                  { " -even": i % 2 === 1 },
                )}
                data-cy={`row-${i}`} // Add data-cy
              >
                {row.cells.map((cell, cellIndex) => {
                  return (
                    <td
                      {...cell.getCellProps()}
                      className="rt-td"
                      key={cellIndex} // Add key
                      data-cy={`cell-${cellIndex}`} // Add data-cy
                    >
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="pagination-top">
        <div className="-pagination">
          <div className="-previous">
            <button
              type="button"
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="-btn"
              data-cy="previous-page" // Add data-cy
            >
              {t("react-previous")}
            </button>
          </div>
          <div className="-center">
            <GridContainer className={classes.gridContainer}>
              <GridItem xs={12} sm={6} md={4}>
                <FormControl
                  fullWidth
                  className={
                    classes.selectFormControl + " " + classes.formControlMargins
                  }
                >
                  <Select
                    MenuProps={{
                      className: classes.selectMenu,
                    }}
                    classes={{
                      select: classes.select,
                    }}
                    value={pageSelect}
                    onChange={(event) => {
                      gotoPage(event.target.value);
                      handlePageSelect(event.target.value);
                    }}
                    inputProps={{
                      name: "pageSelect",
                      id: "page-select",
                      "data-cy": "page-select", // Add data-cy
                    }}
                  >
                    {pageSelectData.map((prop, key) => {
                      return (
                        <MenuItem
                          key={key}
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected,
                          }}
                          value={key}
                        >
                          {t("react-page")} {key + 1}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6} md={4}>
                <FormControl
                  fullWidth
                  className={
                    classes.selectFormControl + " " + classes.formControlMargins
                  }
                >
                  <Select
                    MenuProps={{
                      className: classes.selectMenu,
                    }}
                    classes={{
                      select: classes.select,
                    }}
                    value={numberOfRows}
                    onChange={(event) => {
                      setPageSize(event.target.value);
                      setNumberOfRows(event.target.value);
                    }}
                    inputProps={{
                      name: "numberOfRows",
                      id: "number-of-rows",
                      "data-cy": "page-size-select", // Add data-cy
                    }}
                  >
                    {numberOfRowsData.map((prop) => {
                      return (
                        <MenuItem
                          key={prop}
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected,
                          }}
                          value={prop}
                        >
                          {prop} {t("react-row")}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </GridItem>
            </GridContainer>
          </div>
          <div className="-next">
            <button
              type="button"
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="-btn"
              data-cy="next-page" // Add data-cy
            >
              {t("react-next")}
            </button>
          </div>
        </div>
      </div>

      <div className="pagination-bottom"></div>

      {!!exportToCSVFunction && (
        <Button
          variant="contained"
          color="primary"
          style={{ margin: "20px", maxWidth: "200px" }}
          type="submit"
          onClick={() => {
            exportToCSVFunction(rows);
          }}
          data-cy="export-button" // Add data-cy
        >
          {t("react-export")}
        </Button>
      )}
    </div>
  );
}

function filterGreaterThan(rows, id, filterValue) {
  return rows.filter((row) => {
    const rowValue = row.values[id];
    return rowValue >= filterValue;
  });
}

filterGreaterThan.autoRemove = (val) => typeof val !== "number";

export default React.forwardRef(Table);
